import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  RefreshCw,
  Info,
  Search,
  List,
  GitCompare,
  ShieldCheck,
  CheckCircle,
  XCircle,
  X,
} from "lucide-react";
import { Card, CardContent, Button, Switch, Label } from "@b2b/ui-components";
import { useAuth } from "../../auth/Authprovider";
import { useProductStats } from "../../hooks/useProductStats";
import { useProductList } from "../../hooks/useProductList";
import { RecentActivity } from "../../components/Product/RecentActivity";
import { adminService } from "@b2b/services";

interface ProductDashboardProps {
  role?: "admin" | "seller";
}

const ProductDashboard = ({ role: roleProp }: ProductDashboardProps) => {
  const {
    companyId,
    userRole,
    isLoading: authLoading,
  } = useAuth();
  const navigate = useNavigate();
  const role: "admin" | "seller" =
    roleProp || (userRole === "seller" ? "seller" : "admin");
  const isAdmin = role === "admin";

  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const { stats, loading: statsLoading, refresh: refreshStats } = useProductStats(role);
  const { products, loading: productsLoading, refresh: refreshProducts } = useProductList({
    role,
    autoFetch: true,
  });

  const [autoApproveEnabled, setAutoApproveEnabled] = useState(false);
  const [autoApproveLoading, setAutoApproveLoading] = useState(false);
  const [autoApproveSaving, setAutoApproveSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin || authLoading) return;
    let active = true;
    setAutoApproveLoading(true);
    adminService
      .getAutoApproveProductsSetting()
      .then((res) => {
        if (!active) return;
        setAutoApproveEnabled(Boolean(res?.enabled));
      })
      .catch(() => {
        if (!active) return;
        setAutoApproveEnabled(false);
      })
      .finally(() => {
        if (active) setAutoApproveLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAdmin, authLoading]);

  const handleAutoApproveToggle = async (checked: boolean) => {
    const previous = autoApproveEnabled;
    setAutoApproveEnabled(checked);
    setAutoApproveSaving(true);
    try {
      const res = await adminService.setAutoApproveProductsSetting(checked);
      setAutoApproveEnabled(Boolean(res?.enabled));
      setToast({
        type: "success",
        message: res?.message || (checked ? "Auto-approve enabled" : "Auto-approve disabled"),
      });
      if (checked) {
        await Promise.all([refreshStats(), refreshProducts()]);
      }
    } catch {
      setAutoApproveEnabled(previous);
      setToast({
        type: "error",
        message: "Could not update auto-approve setting. Please try again.",
      });
    } finally {
      setAutoApproveSaving(false);
    }
  };

  const handleRefresh = async () => {
    await Promise.all([refreshStats(), refreshProducts()]);
  };

  const isLoading = statsLoading || productsLoading || authLoading;

  return (
    <div className="space-y-8 mt-5">
      {toast && (
        <div
          className={`fixed top-4 right-4 z-[9999] rounded-lg p-4 shadow-lg ${
            toast.type === "success" ? "bg-green-500 text-white" : "bg-red-500 text-white"
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === "success" ? (
              <CheckCircle className="h-5 w-5 shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 shrink-0" />
            )}
            <p className="flex-1 text-sm font-medium">{toast.message}</p>
            <button
              type="button"
              onClick={() => setToast(null)}
              className="ml-2 shrink-0 hover:opacity-80"
              aria-label="Close notification"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-foreground">
            {isAdmin ? "Product Dashboard" : "My Products Dashboard"}
          </h1>
          <p className="text-muted-foreground">
            {isAdmin
              ? "Overview of all products, statistics, and recent activity"
              : "Overview of your products, statistics, and recent activity"}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          {/* <Button
            variant="primary"
            disabled={!companyId}
            onClick={() => {
              const route = isAdmin
                ? "/admin/create_product"
                : "/seller/create_product";
              navigate(route);
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {!companyId
              ? "Loading..."
              : isAdmin
                ? "Create Product for Seller"
                : "Create Product"}
          </Button> */}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(isAdmin ? "/admin/products/search" : "/seller/products/search")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold">Search Products</h3>
                <p className="text-sm text-muted-foreground">Find products by name, SKU, or category</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => navigate(isAdmin ? "/admin/products/list" : "/seller/products/list")}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <List className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold">View All Products</h3>
                <p className="text-sm text-muted-foreground">Browse and manage all products</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => {
          const route = isAdmin ? "/admin/create_product" : "/seller/create_product";
          navigate(route);
        }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-50 rounded-lg">
                <Plus className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold">Create Product</h3>
                <p className="text-sm text-muted-foreground">Add a new product to the catalog</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div
        className={`grid gap-6 ${isAdmin ? "md:grid-cols-5" : "md:grid-cols-5"}`}
      >
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-8 bg-orange-600 rounded-full" />
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-sm text-muted-foreground">Total Products</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div
                className={`w-2 h-8 ${isAdmin ? "bg-orange-300" : "bg-yellow-300"} rounded-full`}
              />
              <div>
                <p className="text-2xl font-bold">{stats.pending}</p>
                <p className="text-sm text-muted-foreground">
                  {isAdmin ? "Pending Review" : "Pending Approval"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        {isAdmin && (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-8 bg-amber-500 rounded-full" />
                <div>
                  <p className="text-2xl font-bold">
                    {stats.pendingUpdates ?? 0}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Update Pending
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-8 bg-green-600 rounded-full" />
              <div>
                <p className="text-2xl font-bold">{stats.approved}</p>
                <p className="text-sm text-muted-foreground">Approved</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {isAdmin ? (
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-8 bg-blue-600 rounded-full" />
                <div>
                  <p className="text-2xl font-bold">
                    {stats.adminCreated || 0}
                  </p>
                  <p className="text-sm text-muted-foreground">Admin Created</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-8 bg-red-600 rounded-full" />
                  <div>
                    <p className="text-2xl font-bold">{stats.rejected || 0}</p>
                    <p className="text-sm text-muted-foreground">Rejected</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-8 bg-gray-600 rounded-full" />
                  <div>
                    <p className="text-2xl font-bold">{stats.draft || 0}</p>
                    <p className="text-sm text-muted-foreground">Drafts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {isAdmin && (
        <Card className="border-orange-200 bg-orange-50/40">
          <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-100">
                <ShieldCheck className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Auto-approve products</h3>
                <p className="mt-1 max-w-xl text-sm text-gray-600">
                  When enabled, all pending products are approved immediately and every new
                  or submitted product is approved automatically. When off, products stay
                  in pending review until you approve them manually.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 sm:shrink-0">
              <Label htmlFor="auto-approve-products" className="text-sm font-medium text-gray-700">
                {autoApproveEnabled ? "On" : "Off"}
              </Label>
              <Switch
                id="auto-approve-products"
                checked={autoApproveEnabled}
                disabled={autoApproveLoading || autoApproveSaving}
                onCheckedChange={handleAutoApproveToggle}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Info className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-1">
                {isAdmin
                  ? "Admin Product Management"
                  : "Seller Product Management"}
              </h3>
              <p className="text-sm text-blue-700">
                {isAdmin ? (
                  <>
                    As an admin, you can{" "}
                    <strong>create products for sellers</strong> using the
                    "Create Product" button above, or{" "}
                    <strong>review and approve products</strong> submitted by
                    sellers, and open <strong>View All Products</strong> →{" "}
                    <strong>Pending updates</strong> to review seller edits to
                    approved listings. Use the navigation above to search or view
                    all products.
                  </>
                ) : (
                  <>
                    Create products and submit them for admin approval. Products
                    in "Draft" status can be edited and submitted later. Once
                    submitted, products require admin approval before being
                    published.
                  </>
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentActivity products={products} role={role} isLoading={productsLoading} />
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Quick Links</h3>
            <div className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(isAdmin ? "/admin/products/search" : "/seller/products/search")}
              >
                <Search className="mr-2 h-4 w-4" />
                Search Products
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(isAdmin ? "/admin/products/list" : "/seller/products/list")}
              >
                <List className="mr-2 h-4 w-4" />
                View All Products
              </Button>
              {isAdmin && (
                <Button
                  variant="outline"
                  className="w-full justify-start border-amber-200 bg-amber-50/50 hover:bg-amber-50"
                  onClick={() =>
                    navigate("/admin/products/list", {
                      state: { productListTab: "pending-updates" },
                    })
                  }
                >
                  
                  <GitCompare className="mr-2 h-4 w-4 text-amber-700" />
                  Pending seller updates
                  {(stats.pendingUpdates ?? 0) > 0 && (
                    <span className="ml-auto text-xs font-semibold text-amber-800">
                      {stats.pendingUpdates}
                    </span>
                  )}
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => {
                  const route = isAdmin ? "/admin/create_product" : "/seller/create_product";
                  navigate(route);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New Product
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDashboard;

